// Tells the Review page that something else changed the signals.
//
// The same shape as `treeRefreshBus`, and for the same reason: the agent writes
// through the ordinary routes, so a signal it edits lands in the model exactly
// as one a person edits does, and the browser has no idea it happened. The
// write came from the server, not from a click, and the store's own reload
// never ran.
//
// A bus rather than a prop chain because the emitter is the chat panel and the
// listener is the Review page, and neither is an ancestor of the other.
//
// Simpler than the tree's in one way: signals are ONE document per model, not
// one per tree, so there is no target to carry and nothing to key the pending
// set on. A run of edits is still one reload.

/** How long to wait for more emits before reloading. */
const WINDOW_MS = 250;

export type SignalsRefreshHandler = () => void;

type Schedule = (run: () => void, delayMs: number) => void;

export function createSignalsRefreshBus(
  schedule: Schedule = (run, delayMs) => {
    setTimeout(run, delayMs);
  },
  windowMs = WINDOW_MS
) {
  const handlers = new Set<SignalsRefreshHandler>();
  let scheduled = false;

  return {
    subscribe(handler: SignalsRefreshHandler) {
      handlers.add(handler);
      return () => {
        handlers.delete(handler);
      };
    },
    /**
     * Ask for a reload, coalesced.
     *
     * An agent rewriting a group emits per definition. Reloading per emit
     * would queue requests behind each other and re-render the editor under
     * the reader once per write, so emits inside one window collapse.
     */
    emit() {
      if (scheduled) return;
      scheduled = true;
      schedule(() => {
        scheduled = false;
        handlers.forEach((handler) => handler());
      }, windowMs);
    },
  };
}

const bus = createSignalsRefreshBus();

export const emitSignalsRefresh = bus.emit;
export const onSignalsRefresh = bus.subscribe;
